import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Image as ImageIcon, Search } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper per convertire il path dell'immagine
const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  const filename = imageUrl.replace("/uploads/", "");
  return `${API}/images/${filename}?v=${Date.now()}`;
};

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    category_id: "",
    price: 0,
    short_description: "",
    full_description: "",
    duration_days: 30,
    active: true,
    sold_out: false,
    stock: 0,
    low_stock_threshold: 5,
    nutrition_kcal: "",
    nutrition_grassi: "",
    nutrition_grassi_saturi: "",
    nutrition_carboidrati: "",
    nutrition_zuccheri: "",
    nutrition_proteine: "",
    nutrition_sale: "",
  });

  useEffect(() => {
    fetchCategories();
    fetchProducts();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/categories`, {
        params: { active_only: false },
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(response.data);
    } catch (error) {
      console.error("Errore nel caricamento delle categorie:", error);
      toast.error("Errore nel caricamento delle categorie");
    }
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const response = await axios.get(`${API}/products`, {
        params: { active_only: false, admin_view: true },
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(response.data);
    } catch (error) {
      console.error("Errore nel caricamento dei prodotti:", error);
      toast.error("Errore nel caricamento dei prodotti");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : type === "number" ? parseFloat(value) : value,
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const openAddDialog = () => {
    setEditingProduct(null);
    setFormData({
      name: "",
      category_id: categories[0]?.id || "",
      price: 0,
      short_description: "",
      full_description: "",
      duration_days: 30,
      active: true,
      sold_out: false,
      stock: 0,
      low_stock_threshold: 5,
      nutrition_kcal: "",
      nutrition_grassi: "",
      nutrition_grassi_saturi: "",
      nutrition_carboidrati: "",
      nutrition_zuccheri: "",
      nutrition_fibre: "",
      nutrition_proteine: "",
      nutrition_sale: "",
    });
    setImageFile(null);
    setImagePreview(null);
    setShowDialog(true);
  };

  const openEditDialog = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      category_id: product.category_id,
      price: product.price,
      short_description: product.short_description,
      full_description: product.full_description,
      duration_days: product.duration_days,
      active: product.active,
      sold_out: product.sold_out,
      stock: product.stock || 0,
      low_stock_threshold: product.low_stock_threshold || 5,
      nutrition_kcal: product.nutrition_kcal ?? "",
      nutrition_grassi: product.nutrition_grassi ?? "",
      nutrition_grassi_saturi: product.nutrition_grassi_saturi ?? "",
      nutrition_carboidrati: product.nutrition_carboidrati ?? "",
      nutrition_zuccheri: product.nutrition_zuccheri ?? "",
      nutrition_proteine: product.nutrition_proteine ?? "",
      nutrition_sale: product.nutrition_sale ?? "",
    });
    setImageFile(null);
    setImagePreview(product.image_url ? getImageUrl(product.image_url) : null);
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.duration_days < 15 || formData.duration_days > 45) {
      toast.error("La durata deve essere tra 15 e 45 giorni");
      return;
    }

    try {
      const token = localStorage.getItem("admin_token");
      const submitData = new FormData();
      
      Object.keys(formData).forEach((key) => {
        if (key.startsWith("nutrition_")) {
          submitData.append(key, formData[key] === "" ? "" : formData[key]);
        } else {
          submitData.append(key, formData[key]);
        }
      });

      if (imageFile) {
        submitData.append("image", imageFile);
      }

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, submitData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Prodotto aggiornato");
      } else {
        await axios.post(`${API}/products`, submitData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        });
        toast.success("Prodotto aggiunto");
      }

      setShowDialog(false);
      fetchProducts();
    } catch (error) {
      console.error("Errore nell'invio del prodotto:", error);
      toast.error(error.response?.data?.detail || "Errore nell'invio del prodotto");
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo prodotto?")) return;

    try {
      const token = localStorage.getItem("admin_token");
      await axios.delete(`${API}/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Prodotto eliminato");
      fetchProducts();
    } catch (error) {
      console.error("Errore nell'eliminazione del prodotto:", error);
      toast.error("Errore nell'eliminazione del prodotto");
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((c) => c.id === categoryId);
    return category ? category.name : "Sconosciuta";
  };

  if (loading) {
    return (
      <AdminLayout title="Prodotti">
        <div className="flex justify-center py-12">
          <div className="inline-block w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  const placeholderImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150'%3E%3Crect fill='%23111' width='200' height='150'/%3E%3Ctext fill='%23666' font-family='sans-serif' font-size='12' text-anchor='middle' x='100' y='75'%3EPREGIATO%3C/text%3E%3C/svg%3E";

  return (
    <AdminLayout title="Prodotti" data-testid="admin-products">
      {/* Search + Add Button */}
      <div className="mb-6 flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca prodotto..."
            className="w-full bg-zinc-900 border border-zinc-700 rounded-lg pl-10 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-green-500"
            data-testid="search-product-input"
          />
        </div>
        <button
          onClick={openAddDialog}
          className="bg-green-500 text-black px-6 py-3 rounded-lg font-bold hover:bg-green-600 transition-all inline-flex items-center gap-2 shrink-0"
          data-testid="add-product-button"
        >
          <Plus className="w-5 h-5" />
          Aggiungi prodotto
        </button>
      </div>

      {/* Products Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-list">
        {products.length === 0 ? (
          <div className="col-span-full text-center py-12 bg-zinc-900 border border-zinc-800 rounded-lg">
            <p className="text-zinc-400">Nessun prodotto presente</p>
          </div>
        ) : (
          products
            .filter((p) => !searchQuery || p.name.toLowerCase().includes(searchQuery.toLowerCase()))
            .map((product) => {
            const imageUrl = product.image_url
              ? getImageUrl(product.image_url)
              : placeholderImage;

            return (
              <div
                key={product.id}
                className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden"
                data-testid={`product-${product.id}`}
              >
                <div className="relative aspect-[4/3]">
                  <img
                    src={imageUrl}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  {product.sold_out && (
                    <span className="absolute top-2 right-2 bg-red-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Esaurito
                    </span>
                  )}
                  {!product.active && (
                    <span className="absolute top-2 left-2 bg-zinc-700 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      Non attivo
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-bold text-white mb-1" data-testid="product-name">{product.name}</h3>
                  <p className="text-zinc-400 text-sm mb-2">{getCategoryName(product.category_id)}</p>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xl font-bold text-white">€{product.price.toFixed(2)}</span>
                    <span className="text-xs text-zinc-500">{product.duration_days} giorni</span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditDialog(product)}
                      className="flex-1 bg-yellow-500 text-black py-2 rounded-lg font-bold hover:bg-yellow-600 transition-all inline-flex items-center justify-center gap-2"
                      data-testid="edit-product-button"
                    >
                      <Edit className="w-4 h-4" />
                      Modifica
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="flex-1 bg-red-500 text-white py-2 rounded-lg font-bold hover:bg-red-600 transition-all inline-flex items-center justify-center gap-2"
                      data-testid="delete-product-button"
                    >
                      <Trash2 className="w-4 h-4" />
                      Elimina
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingProduct ? "Modifica prodotto" : "Nuovo prodotto"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="product-form">
            <div>
              <label className="block text-white font-semibold mb-2">Immagine</label>
              <div className="flex items-center gap-4">
                {imagePreview && (
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-32 h-24 object-cover rounded border border-zinc-700"
                  />
                )}
                <label className="flex-1 bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white cursor-pointer hover:border-green-500 transition-colors inline-flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  <span>{imageFile ? imageFile.name : "Scegli immagine"}</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="hidden"
                    data-testid="image-input"
                  />
                </label>
              </div>
            </div>

            <div>
              <label className="block text-white font-semibold mb-2" htmlFor="name">Nome *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                required
                data-testid="product-name-input"
              />
            </div>

            <div>
              <label className="block text-white font-semibold mb-2" htmlFor="category_id">Categoria *</label>
              <select
                id="category_id"
                name="category_id"
                value={formData.category_id}
                onChange={handleInputChange}
                className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                required
                data-testid="product-category-select"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white font-semibold mb-2" htmlFor="price">Prezzo (\u20ac) *</label>
                <input
                  type="number"
                  id="price"
                  name="price"
                  value={formData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                  required
                  data-testid="product-price-input"
                />
              </div>
              <div>
                <label className="block text-white font-semibold mb-2" htmlFor="duration_days">Durata (giorni) *</label>
                <input
                  type="number"
                  id="duration_days"
                  name="duration_days"
                  value={formData.duration_days}
                  onChange={handleInputChange}
                  min="15"
                  max="45"
                  className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                  required
                  data-testid="product-duration-input"
                />
              </div>
            </div>

            {/* Stock Management */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-white font-semibold mb-2" htmlFor="stock">Quantità in magazzino</label>
                <input
                  type="number"
                  id="stock"
                  name="stock"
                  value={formData.stock}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                  data-testid="product-stock-input"
                />
              </div>
              <div>
                <label className="block text-white font-semibold mb-2" htmlFor="low_stock_threshold">Soglia scorte basse</label>
                <input
                  type="number"
                  id="low_stock_threshold"
                  name="low_stock_threshold"
                  value={formData.low_stock_threshold}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                  data-testid="product-threshold-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-white font-semibold mb-2" htmlFor="short_description">Descrizione breve *</label>
              <textarea
                id="short_description"
                name="short_description"
                value={formData.short_description}
                onChange={handleInputChange}
                rows="2"
                className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                required
                data-testid="product-short-desc-input"
              />
            </div>

            <div>
              <label className="block text-white font-semibold mb-2" htmlFor="full_description">Descrizione completa *</label>
              <textarea
                id="full_description"
                name="full_description"
                value={formData.full_description}
                onChange={handleInputChange}
                rows="4"
                className="w-full bg-black border border-zinc-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-green-500"
                required
                data-testid="product-full-desc-input"
              />
            </div>

            {/* Tabella Nutrizionale */}
            <div className="border border-zinc-700 rounded-lg p-4">
              <h3 className="text-white font-semibold mb-3">Valori Nutrizionali per 100g</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Kcal</label>
                  <input type="number" step="0.1" name="nutrition_kcal" value={formData.nutrition_kcal} onChange={handleInputChange} placeholder="-" className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="nutrition-kcal" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Grassi (g)</label>
                  <input type="number" step="0.1" name="nutrition_grassi" value={formData.nutrition_grassi} onChange={handleInputChange} placeholder="-" className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="nutrition-grassi" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">di cui Saturi (g)</label>
                  <input type="number" step="0.1" name="nutrition_grassi_saturi" value={formData.nutrition_grassi_saturi} onChange={handleInputChange} placeholder="-" className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="nutrition-saturi" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Carboidrati (g)</label>
                  <input type="number" step="0.1" name="nutrition_carboidrati" value={formData.nutrition_carboidrati} onChange={handleInputChange} placeholder="-" className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="nutrition-carbo" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">di cui Zuccheri (g)</label>
                  <input type="number" step="0.1" name="nutrition_zuccheri" value={formData.nutrition_zuccheri} onChange={handleInputChange} placeholder="-" className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="nutrition-zuccheri" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Proteine (g)</label>
                  <input type="number" step="0.1" name="nutrition_proteine" value={formData.nutrition_proteine} onChange={handleInputChange} placeholder="-" className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="nutrition-proteine" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-sm mb-1">Sale (g)</label>
                  <input type="number" step="0.01" name="nutrition_sale" value={formData.nutrition_sale} onChange={handleInputChange} placeholder="-" className="w-full bg-black border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-green-500" data-testid="nutrition-sale" />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active"
                  name="active"
                  checked={formData.active}
                  onChange={handleInputChange}
                  className="w-5 h-5"
                  data-testid="product-active-checkbox"
                />
                <label htmlFor="active" className="text-white font-semibold">Attivo</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="sold_out"
                  name="sold_out"
                  checked={formData.sold_out}
                  onChange={handleInputChange}
                  className="w-5 h-5"
                  data-testid="product-soldout-checkbox"
                />
                <label htmlFor="sold_out" className="text-white font-semibold">Esaurito</label>
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex-1 bg-green-500 text-black py-3 rounded-lg font-bold hover:bg-green-600 transition-all"
                data-testid="save-product-button"
              >
                {editingProduct ? "Aggiorna" : "Aggiungi"}
              </button>
              <button
                type="button"
                onClick={() => setShowDialog(false)}
                className="flex-1 bg-zinc-800 text-white py-3 rounded-lg font-bold hover:bg-zinc-700 transition-all"
                data-testid="cancel-product-button"
              >
                Annulla
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminProducts;
